<template>
  <div class="app">
    <NuxtLayout>
      <NuxtPage />
    </NuxtLayout>
  </div>
</template>

<script setup async lang="ts">
import usePageStore from '~/stores/page';
import useGlobalConfigStore from '~/stores/global';

// needed to detect when scrolling ended
import '@af-utils/scrollend-polyfill';
import extendedScroll from '~/utils/extendedScroll';
import useBreakpointsStore from '~/stores/breakpoints';
import { useRouter } from '#app';
import { useSmartDataRefresh } from '~/composables/useSmartDataRefresh';

useBreakpointsStore();

// get all global settings first
const globalConfigStore = useGlobalConfigStore();
const { error } = await useAsyncData('global_config', globalConfigStore.fetchSettings);
const { $isTransitioning } = useNuxtApp();
const router = useRouter();
const { isEnv } = useUrl();
const devMode = import.meta.dev || (import.meta.client && isEnv('dev'));
const { $event } = useNuxtApp();

// initialize a datalayer ASAP
useHead({
  script: [
    {
      innerHTML: 'window.dataLayer = window.dataLayer || [];',
      tagPosition: 'head',
    },
  ],
});

if (error.value) {
  throw error.value;
}

// setup the page store
usePageStore();

// util to help identify window.scrollTo vs a user mouse scroll
onMounted(extendedScroll);

// this is the scroll logic for all pages (set in router-guard)
onMounted(() => {
  watch(
    () => $isTransitioning.value,
    () => {
      nextTick(() => {
        const scrollToOptions = (router.options.history.state.scroll as object) || {
          top: 0,
          left: 0,
        };

        const currentTop = document.documentElement.scrollTop || document.body.scrollTop;
        if (currentTop !== scrollToOptions.top) {
          window.scrollTo({ ...scrollToOptions, behavior: 'auto' });
        }
      });
    },
  );
});

if (import.meta.client) {

  const { isRefreshing } = useSmartDataRefresh({
    thresholdMin: devMode ? 2.5 : 10,
    useJitter: true,
    checkInactivity: true,
  });

  watch(isRefreshing, (loading) => {
    if (loading) {
      $event('snackbar:show', { message: 'Pagina wordt bijgewerkt...' });
    } else {
      $event('snackbar:hide');
    }
  });
}

useDefaultSchema();

// add prefetch link for cookiebanner related scripts/assets
const prefetchLinks = [
  { rel: 'prefetch', href: 'https://ccm.npo.nl' },
  { rel: 'preconnect', href: 'https://cdn.optoutadvertising.com' },
  { rel: 'dns-prefetch', href: 'https://cdn.optoutadvertising.com' },
];

useHead({
  link: prefetchLinks,
});
</script>

<style lang="postcss" scoped>
.app {
  background-color: hsl(var(--color-neutral-white));
}
</style>
